import { useEffect, useMemo, useRef, useState } from "react";
import { useScribbleLobby } from "./useScribbleLobby";
import { strokesToArray, chatToArray } from "../lib/scribbleGame";
import {
  tabooWordsFromGame,
  shownWordsFromGame,
  releasedTabooIndices,
  isWordReleasedToPlayers,
  correctGuessersFromGame,
  isActiveGuessPhase,
} from "../lib/tabooGame";

export function useScribbleGame() {
  const lobbyHook = useScribbleLobby({ autoJoin: true });
  const { lobbyName, userId, name, lobby: subscribedLobby, players, drawers: drawerIds, isHost, hostUserId } =
    lobbyHook;

  const [remainingSec, setRemainingSec] = useState(0);
  const timerAdvancedRef = useRef(false);

  const game = subscribedLobby?.game || {};
  const turnIndex = Number(game.turnIndex) || 0;
  const currentDrawerId = String(game.currentDrawerId || drawerIds[turnIndex] || "");
  const turnPhase = game.turnPhase || "pick_word";
  const isCurrentDrawer = Boolean(userId && currentDrawerId && userId === currentDrawerId);
  const currentDrawer = players.find((p) => p.id === currentDrawerId);
  const timerSeconds = Math.max(
    30,
    Number(game.timerSeconds ?? subscribedLobby?.timerSeconds) || 60
  );
  const timerEndsAt = game.timerEndsAt != null ? Number(game.timerEndsAt) : null;

  const strokes = useMemo(
    () => strokesToArray(subscribedLobby?.canvas?.strokes),
    [subscribedLobby?.canvas?.strokes]
  );
  const chatMessages = useMemo(
    () => chatToArray(subscribedLobby?.chat),
    [subscribedLobby?.chat]
  );
  const shownWordKeys = useMemo(
    () => shownWordsFromGame(game),
    [game.shownWords, game.selectedWord]
  );

  useEffect(() => {
    const mode = String(subscribedLobby?.gameMode || "").toLowerCase();
    const isTabooMode = mode === "taboo";
    const overallTimerPaused = game.overallTimerPaused === true;
    const activePhase = turnPhase === "drawing" || turnPhase === "guessing";
    
    if (isTabooMode && overallTimerPaused) {
      const remaining = Math.max(0, Number(game.overallTimerRemaining) || 0);
      setRemainingSec(remaining);
      timerAdvancedRef.current = false;
      return undefined;
    }
    
    if (!timerEndsAt || !activePhase) {
      timerAdvancedRef.current = false;
      setRemainingSec(timerSeconds);
      return undefined;
    }
    
    const tick = () => {
      const left = Math.max(0, Math.ceil((timerEndsAt - Date.now()) / 1000));
      setRemainingSec(left);
      if (left <= 0 && !timerAdvancedRef.current) {
        const tabooActive = isTabooMode && isActiveGuessPhase(turnPhase);
        const scribbleEnd = !isTabooMode && !game.guessedBy;
        if (tabooActive || scribbleEnd) {
          timerAdvancedRef.current = true;
          import("../lib/scribbleGame").then(({ enterRoundEnd, advanceTurn }) => {
            const fn = isTabooMode ? enterRoundEnd : advanceTurn;
            fn(lobbyName).catch(() => {});
          });
        }
      }
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [timerEndsAt, turnPhase, timerSeconds, lobbyName, game.guessedBy, game.overallTimerPaused, game.overallTimerRemaining, subscribedLobby?.gameMode]);

  return {
    ...lobbyHook,
    game,
    turnIndex,
    currentDrawerId,
    currentDrawerName: currentDrawer?.name || "Drawer",
    turnPhase,
    isCurrentDrawer,
    selectedWord: String(game.selectedWord || ""),
    selectedHint: String(game.selectedHint || ""),
    guessedBy: game.guessedBy != null ? String(game.guessedBy) : null,
    guessedByName: String(game.guessedByName || ""),
    guessedPoints: Math.max(0, Number(game.guessedPoints) || 0),
    timerSeconds,
    timerEndsAt,
    remainingSec,
    overallTimerTotal: Math.max(0, Number(game.overallTimerTotal) || 0),
    overallTimerRemaining: Math.max(0, Number(game.overallTimerRemaining) || 0),
    overallTimerPaused: game.overallTimerPaused === true,
    strokes,
    chatMessages,
    isHost,
    hostUserId,
    gameMode: String(subscribedLobby?.gameMode || "manager").toLowerCase(),
    tabooWords: tabooWordsFromGame(game),
    wordReleased: isWordReleasedToPlayers(game),
    releasedTabooIndices: releasedTabooIndices(game),
    flipIndex: Math.max(0, Number(game.flipIndex) || 0),
    shownWordKeys,
    lobbyPhase: subscribedLobby?.phase || "lobby",
    correctGuessers: correctGuessersFromGame(game),
  };
}
